import { ProgramTask, RPBenchmarkStatus, generatePath } from '@cibo/core'
import { Loading, useWindowTitle } from '@cibo/profile'
import { BenchmarkStatus, DetailsLocked } from '@cibo/programs'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useProgramEngagementContext } from '../../features/ProgramEngagement'
import { EngagementRouteParams } from '../../navigation/types/engagementRoutes'
import { PROGRAM_ROUTES } from '../../navigation/types/routes'
import { SelectFields } from './SelectFields'
import { useNavigateToNextTask } from './useNavigateToNextTask'

export const Task = ({ taskId }: Pick<EngagementRouteParams, 'stepId' | 'taskId'>) => {
  const { t } = useTranslation('@cibo/impact/Task')
  const { onSuccess, ownerName, engagement, program, userRole } = useProgramEngagementContext()
  const navigateToNextTask = useNavigateToNextTask()

  const task = useMemo(() => {
    if (!!program.data?.programId && !!taskId) {
      return program.data.task(taskId)
    }
  }, [program.data?.programId, taskId])

  const windowTitle = useMemo(
    () =>
      t('windowTitle', {
        programName: program.data?.title,
        taskName: program.data?.taskContent(`${taskId}`)?.content?.title,
      }),
    [task?.id, program.dataUpdatedAt]
  )
  useWindowTitle(windowTitle, { format: false })

  if (program.isLoading || engagement.isLoading) {
    return <Loading />
  }

  if (!engagement.data) {
    return <>NOT FOUND</>
  }

  return task?.rule.type === 'benchmarkStatus' ? (
    <BenchmarkStatus
      //key needed to reset form state when switching between tasks
      key={taskId}
      engagement={engagement.data}
      onSuccess={onSuccess}
      ownerName={ownerName}
      task={task as ProgramTask<RPBenchmarkStatus>}
      userRole={userRole}
    />
  ) : task?.rule.type === 'detailsLocked' ? (
    <DetailsLocked
      abandonPath={generatePath(PROGRAM_ROUTES.ROOT)}
      engagement={engagement.data}
      onSuccess={onSuccess}
      ownerName={ownerName}
      task={task}
      userRole={userRole}
      navigateToNextTask={navigateToNextTask}
    />
  ) : task?.rule.type === 'fieldsSelected' ? (
    <SelectFields
      engagement={engagement.data}
      onSuccess={onSuccess}
      ownerName={ownerName}
      task={task as ProgramTask<RPBenchmarkStatus>}
      userRole={userRole}
    />
  ) : (
    <>Unknown rule type</>
  )
}
